import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { LayoutDashboard, UserCheck, Film, Settings, LogOut, Menu, Ticket } from 'lucide-react';
import OrganizerBookings from './OrganizerBookings';
import { Movie, Screening } from '../types';
import OrganizerDashboard from './OrganizerDashboard';
import OrganizerEventManagement from './OrganizerEventManagement';
import OrganizerSettings from './OrganizerSettings';
import OrganizerValidatorManagement from './OrganizerValidatorManagement';
import AddNewEvent from './AddNewEvent';
import OrganizerEventDetails from './OrganizerEventDetails';

interface OrganizerPanelProps {
  onClose: () => void;
  onNavigate: (page: string) => void;
}

type OrganizerRow = {
  id: number;
  name: string;
  image_url: string;
  contact_email: string | null;
  contact_number: string | null;
  logo_url: string | null;
  owner_id: string | null;
  terms_and_conditions: string | null;
  gallery_images: string[] | null;
  is_gallery_enabled: boolean | null;
}

export default function OrganizerPanel({ onClose, onNavigate }: OrganizerPanelProps) {
  const [loading, setLoading] = useState(true);
  const [organizer, setOrganizer] = useState<OrganizerRow | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [isGalleryEnabled, setIsGalleryEnabled] = useState(true);
  const [movies, setMovies] = useState<Movie[]>([]);
  const [screeningsByMovie, setScreeningsByMovie] = useState<Record<number, Screening[]>>({});
  const [activeSection, setActiveSection] = useState<'dashboard' | 'validators' | 'events' | 'event-details' | 'bookings' | 'settings'>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [validators, setValidators] = useState<Array<{ user_id: string; email: string }>>([]);
  const [registrationsCount, setRegistrationsCount] = useState(0);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedMovieId, setSelectedMovieId] = useState<number | null>(null);
  const [selectedScreeningId, setSelectedScreeningId] = useState<number | null>(null);
  const [dashboardDays, setDashboardDays] = useState<number>(30);
  const [dashboardMovieId, setDashboardMovieId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: userInfo } = await supabase.auth.getUser();
      const uid = userInfo?.user?.id || null;
      const email = userInfo?.user?.email || '';
      if (!uid) {
        toast.error('Not signed in');
        setLoading(false);
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', uid)
        .maybeSingle();
      setUserRole((profile as { role?: string } | null)?.role || null);
      const role = (profile as { role?: string } | null)?.role || null;
      if (role !== 'organizer' && role !== 'admin') {
        setLoading(false);
        return;
      }
      const { data: rows } = await supabase
        .from('organizers')
        .select('*')
        .eq('owner_id', uid)
        .limit(1);
      let row = rows && rows.length ? (rows[0] as OrganizerRow) : null;
      if (!row) {
        const { data: inserted, error } = await supabase
          .from('organizers')
          .insert([{ name: email.split('@')[0], image_url: 'https://i.postimg.cc/02Y8y6Yt/Whats-App-Image-2025-03-24-at-13-08-06-1.jpg', owner_id: uid, contact_email: email, contact_number: null }])
          .select('*')
          .single();
        if (error) {
          toast.error('Failed to initialize organizer');
          setLoading(false);
          return;
        }
        row = inserted as OrganizerRow;
      }
      setOrganizer(row);
      setName(row.name);
      setImageUrl(row.image_url);
      setContactEmail(row.contact_email || '');
      setContactNumber(row.contact_number || '');
      setLogoUrl(row.logo_url || '');
      setTermsAndConditions(row.terms_and_conditions || '');
      setGalleryImages(row.gallery_images || []);
      setIsGalleryEnabled(row.is_gallery_enabled !== false);
      setLoading(false);
    })();
  }, []);

  const saveOrganizer = async () => {
    if (!organizer) return;
    const { error } = await supabase
      .from('organizers')
      .update({
        name,
        image_url: imageUrl,
        contact_email: contactEmail || null,
        contact_number: contactNumber || null,
        logo_url: logoUrl || null,
        terms_and_conditions: termsAndConditions || null,
        gallery_images: galleryImages,
        is_gallery_enabled: isGalleryEnabled
      })
      .eq('id', organizer.id);
    if (error) {
      toast.error('Failed to save organizer');
      return;
    }
    toast.success('Organizer details saved');
  };



  const assignEditorWithEmail = async (enable: boolean, email: string) => {
    if (!email) {
      toast.error('Enter an email');
      return;
    }
    try {
      const { data, error } = await supabase.rpc('assign_editor_by_email', { target_email: email, enable });
      if (error) {
        toast.error('Failed to update editor');
        return;
      }
      if (data) toast.success(enable ? 'Editor assigned' : 'Editor removed');
    } catch {
      toast.error('Failed to update editor');
    }
  }



  useEffect(() => {
    (async () => {
      if (!organizer) return;
      const { data: ms } = await supabase
        .from('movies')
        .select('*')
        .eq('organizer_id', organizer.id)
        .order('created_at', { ascending: false });
      setMovies((ms || []) as Movie[]);
      const ids = ((ms || []) as Movie[]).map((m) => m.id);
      if (ids.length) {
        const { data: ss } = await supabase
          .from('screenings')
          .select('*')
          .in('movie_id', ids)
          .order('start_time', { ascending: true });
        const map: Record<number, Screening[]> = {};
        for (const s of ((ss || []) as Screening[])) {
          const mid = s.movie_id as number;
          if (!map[mid]) map[mid] = [];
          map[mid].push(s);
        }
        setScreeningsByMovie(map);
        const screeningIds = ((ss || []) as Screening[]).map(x => x.id);
        if (screeningIds.length) {
          const { data: bookings } = await supabase
            .from('bookings')
            .select('id,screening_id')
            .in('screening_id', screeningIds);
          const bookingIds = ((bookings || []) as { id: number }[]).map(b => b.id);
          if (bookingIds.length) {
            const { count } = await supabase
              .from('booking_seats')
              .select('*', { count: 'exact', head: true })
              .in('booking_id', bookingIds);
            setRegistrationsCount(count || 0);
          } else {
            setRegistrationsCount(0);
          }
        } else {
          setRegistrationsCount(0);
        }
      } else {
        setScreeningsByMovie({});
        setRegistrationsCount(0);
      }
    })();
  }, [organizer]);

  const refreshData = useCallback(async () => {
    if (!organizer) return;

    const { data: ms } = await supabase
      .from('movies')
      .select('*')
      .eq('organizer_id', organizer.id)
      .order('created_at', { ascending: false });
    setMovies((ms || []) as Movie[]);

    const ids = ((ms || []) as Movie[]).map((m) => m.id);
    if (ids.length) {
      const { data: ss } = await supabase
        .from('screenings')
        .select('*')
        .in('movie_id', ids)
        .order('start_time', { ascending: true });

      const map: Record<number, Screening[]> = {};
      for (const s of ((ss || []) as Screening[])) {
        const mid = s.movie_id as number;
        if (!map[mid]) map[mid] = [];
        map[mid].push(s);
      }
      setScreeningsByMovie(map);
    } else {
      setScreeningsByMovie({});
    }
  }, [organizer]);

  const loadValidators = useCallback(async () => {
    if (!organizer) return;
    const { data: ovMap } = await supabase
      .from('organizer_validators')
      .select('user_id')
      .eq('organizer_id', organizer.id);
    const vIds = ((ovMap || []) as { user_id: string }[]).map(r => r.user_id);
    if (vIds.length) {
      const { data: vProfiles } = await supabase
        .from('profiles')
        .select('id,email')
        .in('id', vIds);
      setValidators(((vProfiles || []) as { id: string; email: string }[]).map(p => ({ user_id: p.id, email: p.email })));
    } else {
      setValidators([]);
    }
  }, [organizer]);

  useEffect(() => {
    (async () => { await loadValidators(); })();
  }, [loadValidators]);

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center">
      {userRole !== 'organizer' && userRole !== 'admin' ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-hidden">
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Organizer Panel</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">×</button>
          </div>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Access Denied</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">You do not have permission to access the organizer panel.</p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">Back</button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Organizer Panel</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">×</button>
          </div>

          {showAddEvent && (
            <AddNewEvent
              onClose={() => setShowAddEvent(false)}
              onEventAdded={refreshData}
              organizerId={organizer?.id}
            />
          )}
          <div className="relative flex h-[calc(90vh-56px)] w-full overflow-hidden">
            <aside className="w-64 h-full flex-shrink-0 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 hidden lg:flex flex-col justify-between p-4">
              <div className="flex flex-col gap-8">
                {/* Organizer Profile */}
                <div className="flex gap-3 items-center px-3 py-2">
                  <div
                    className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10"
                    style={{ backgroundImage: `url("${organizer?.image_url || 'https://via.placeholder.com/150'}")` }}
                  />
                  <div className="flex flex-col">
                    <h1 className="text-gray-900 dark:text-white text-sm font-medium leading-normal">{organizer?.name || 'Organizer'}</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-xs font-normal leading-normal">Organizer</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <button onClick={() => setActiveSection('dashboard')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'dashboard' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                    <LayoutDashboard className="h-4 w-4" />
                    <p className="text-sm font-medium">Dashboard</p>
                  </button>
                  <button onClick={() => setActiveSection('bookings')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'bookings' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                    <Ticket className="h-4 w-4" />
                    <p className="text-sm font-medium">Bookings</p>
                  </button>
                  <button onClick={() => setActiveSection('validators')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'validators' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                    <UserCheck className="h-4 w-4" />
                    <p className="text-sm font-medium">Validator Management</p>
                  </button>
                  <button onClick={() => setActiveSection('events')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'events' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                    <Film className="h-4 w-4" />
                    <p className="text-sm font-medium">Event Management</p>
                  </button>
                  <button onClick={() => setActiveSection('settings')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'settings' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                    <Settings className="h-4 w-4" />
                    <p className="text-sm font-medium">Settings</p>
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="w-full">
                  <button onClick={onClose} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">
                    <LogOut className="h-4 w-4" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Back</p>
                  </button>
                </div>
              </div>
            </aside>
            {sidebarOpen && (
              <div className="fixed inset-0 z-30 lg:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
                <aside className="absolute top-0 left-0 bottom-0 w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col justify-between p-4">
                  <div className="flex flex-col gap-8">
                    {/* Organizer Profile */}
                    <div className="flex gap-3 items-center px-3 py-2">
                      <div
                        className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10"
                        style={{ backgroundImage: `url("${organizer?.image_url || 'https://via.placeholder.com/150'}")` }}
                      />
                      <div className="flex flex-col">
                        <h1 className="text-gray-900 dark:text-white text-sm font-medium leading-normal">{organizer?.name || 'Organizer'}</h1>
                        <p className="text-gray-600 dark:text-gray-400 text-xs font-normal leading-normal">Organizer</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button onClick={() => { setActiveSection('dashboard'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'dashboard' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                        <LayoutDashboard className="h-4 w-4" />
                        <p className="text-sm font-medium">Dashboard</p>
                      </button>
                      <button onClick={() => { setActiveSection('bookings'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'bookings' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                        <Ticket className="h-4 w-4" />
                        <p className="text-sm font-medium">Bookings</p>
                      </button>
                      <button onClick={() => { setActiveSection('validators'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'validators' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                        <UserCheck className="h-4 w-4" />
                        <p className="text-sm font-medium">Validator Management</p>
                      </button>
                      <button onClick={() => { setActiveSection('events'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'events' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                        <Film className="h-4 w-4" />
                        <p className="text-sm font-medium">Event Management</p>
                      </button>
                      <button onClick={() => { setActiveSection('settings'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${activeSection === 'settings' ? 'bg-indigo-600/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                        <Settings className="h-4 w-4" />
                        <p className="text-sm font-medium">Settings</p>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="w-full">
                      <button onClick={() => { setSidebarOpen(false); onClose(); }} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">
                        <LogOut className="h-4 w-4" />
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Back</p>
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            )}
            <main className="flex-1 h-full flex flex-col min-w-0">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-3 bg-gray-100 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  <button onClick={() => setSidebarOpen(true)} className="flex items-center justify-center rounded-full h-10 w-10 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white lg:hidden">
                    <Menu className="h-5 w-5" />
                  </button>
                  {activeSection !== 'events' && (
                    <h3 className="text-gray-900 dark:text-white text-lg font-semibold">{activeSection === 'dashboard' ? 'Dashboard Overview' : activeSection === 'bookings' ? 'Bookings' : activeSection === 'validators' ? 'Validator Management' : activeSection === 'event-details' ? 'Event Details' : 'Settings'}</h3>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {activeSection === 'dashboard' && (
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        value={dashboardDays}
                        onChange={e => setDashboardDays(Number(e.target.value))}
                        className="h-10 min-w-[140px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                      >
                        <option value={1}>Today</option>
                        <option value={7}>Last 7 Days</option>
                        <option value={30}>Last 30 Days</option>
                        <option value={90}>Last 90 Days</option>
                      </select>
                      <select
                        value={dashboardMovieId ?? ''}
                        onChange={e => setDashboardMovieId(e.target.value ? Number(e.target.value) : null)}
                        className="h-10 min-w-[180px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                      >
                        <option value="">All Events</option>
                        {movies.map(m => (
                          <option key={m.id} value={m.id}>{m.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(userRole === 'organizer' || userRole === 'admin') && activeSection === 'dashboard' ? (
                    <button
                      onClick={() => setShowAddEvent(true)}
                      className="flex items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
                    >
                      Create New Event
                    </button>
                  ) : (
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{organizer?.name || 'Organizer'}</p>
                  )}
                </div>
              </div>
              <div className="flex-1 w-full p-6 space-y-6 overflow-y-auto">
                {loading ? (
                  <p className="text-gray-700 dark:text-gray-300">Loading...</p>
                ) : (
                  <>
                    {activeSection === 'dashboard' && (
                      <OrganizerDashboard
                        movies={movies}
                        validators={validators}
                        registrationsCount={registrationsCount}
                        screeningsByMovie={screeningsByMovie}
                        organizerName={organizer?.name || ''}
                        organizerId={organizer?.id || null}
                        days={dashboardDays}
                        movieId={dashboardMovieId}
                      />
                    )}
                    {activeSection === 'bookings' && (
                      <OrganizerBookings organizerId={organizer?.id || null} />
                    )}
                    {activeSection === 'validators' && (
                      <OrganizerValidatorManagement organizerId={organizer?.id || null} validators={validators} onRefresh={loadValidators} />
                    )}
                    {activeSection === 'events' && (
                      <OrganizerEventManagement
                        movies={movies}
                        screeningsByMovie={screeningsByMovie}
                        onViewDetails={(movieId, screeningId) => { setSelectedMovieId(movieId); setSelectedScreeningId(screeningId ?? null); setActiveSection('event-details'); }}
                        onCreateNew={() => setShowAddEvent(true)}
                      />
                    )}
                    {activeSection === 'event-details' && selectedMovieId !== null && (
                      <OrganizerEventDetails movieId={selectedMovieId} initialScreeningId={selectedScreeningId} onBack={() => { setActiveSection('events'); setSelectedMovieId(null); setSelectedScreeningId(null); }} onNavigate={onNavigate} />
                    )}
                    {activeSection === 'settings' && (
                      <OrganizerSettings
                        name={name}
                        imageUrl={imageUrl}
                        contactEmail={contactEmail}
                        contactNumber={contactNumber}
                        logoUrl={logoUrl}
                        onChangeName={setName}
                        onChangeImageUrl={setImageUrl}
                        onChangeContactEmail={setContactEmail}
                        onChangeContactNumber={setContactNumber}
                        onChangeLogoUrl={setLogoUrl}
                        termsAndConditions={termsAndConditions}
                        onChangeTermsAndConditions={setTermsAndConditions}
                        onSave={saveOrganizer}
                        onClose={onClose}
                        onAssignEditor={(enable, email) => assignEditorWithEmail(enable, email)}
                        galleryImages={galleryImages}
                        onChangeGalleryImages={setGalleryImages}
                        isGalleryEnabled={isGalleryEnabled}
                        onChangeIsGalleryEnabled={setIsGalleryEnabled}
                      />
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        </div>
      )}
    </div>
  );
}
